import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ctCompute, moneyIn } from './contractsCalc'
import type { CtPriceBook, CtSelections } from './crmApi'
import {
  AGREEMENT_CSS, CONTACT_HTML, FACTS_HTML, FOOTER_HTML, JOURNEY_HTML, KEY_TERMS_HTML, NAV_HTML, PARTIES_HTML, SOW_HTML, TERMS_HTML,
} from './contractContent'

/**
 * PUBLIC CONTRACT PAGE v2 (Lee, 6 Oct 2026) — /contract/:slug?token=
 * Rebuilt to match Lee's wequote-customer-agreement.html as closely as
 * possible: its CSS/fonts verbatim, the tube-line journey, fact chips, pill
 * nav, Part boards, the full Statement of Works, the full Terms of Service
 * as a COLLAPSIBLE at the end, his contact grid and the red WEQUOTE footer
 * band. Part A is the live piece: each section is either FIXED by staff
 * (default — shows exactly what we picked at our price) or opened for the
 * customer to choose; totals, the discount matrix and the signature block
 * all recalc live. Signing locks the page.
 */

const api = (slug: string, token: string, path: string) => `/api/public/contract/${slug}/${path}?token=${encodeURIComponent(token)}`

const EXTRA_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Mulish:wght@500;700;800;900&family=Work+Sans:wght@400;500;600;700&display=swap');
/* MOBILE (Lee, 13 Oct 2026): clean single-column reading on phones. */
@media (max-width: 640px){
  .wrap{padding:14px 10px !important}
  header h1{font-size:26px !important;line-height:1.15}
  .part-body{padding:14px 12px !important}
  .tw{overflow-x:auto;-webkit-overflow-scrolling:touch}
  .tw table{min-width:520px}
  table.mx{display:block;overflow-x:auto;-webkit-overflow-scrolling:touch}
  .sig{display:grid !important;grid-template-columns:1fr !important;gap:12px}
  .sigpad{width:100% !important;height:auto}
  .signbtn{width:100%;text-align:center}
  .siginput{flex:1 1 100% !important}
  .totalrow{flex-direction:column;align-items:flex-start;gap:8px}
  .bigtotal{font-size:24px !important}
  .pickgrid{grid-template-columns:1fr 1fr}
  .fixedrow{flex-wrap:wrap}
  .savebanner{flex-wrap:wrap}
}
.pickgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(175px,1fr));gap:10px;margin:10px 0}
.pick{border:1.5px solid var(--rule);border-radius:12px;background:#fff;padding:13px 14px;text-align:left;font-family:var(--body);cursor:pointer}
.pick[disabled]{cursor:default}
.pick.on{border:2.5px solid var(--red);background:#fff6f7}
.pick .nm{display:block;font:800 14px var(--display);color:var(--navy)}
.pick .pr{display:block;font:900 17px var(--display);color:var(--red-ink);margin-top:3px}
.pick .pr small{font:600 11px var(--body);color:var(--muted)}
.pick ul{margin:8px 0 0;padding-left:16px;font-size:12px;color:var(--slate)}
.fixedrow{display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:var(--band);border:1.5px solid var(--rule);border-radius:12px;padding:12px 14px;margin:10px 0}
.fixedrow .nm{font:800 14px var(--display);color:var(--navy);flex:1;min-width:160px}
.fixedrow .pr{font:900 15px var(--display);color:var(--navy);white-space:nowrap}
.tagfix{font:800 10px var(--display);letter-spacing:.08em;text-transform:uppercase;color:var(--muted);background:#fff;border:1.5px solid var(--rule);border-radius:999px;padding:3px 9px;white-space:nowrap}
.step{display:inline-flex;align-items:center;gap:12px}
.step button{width:34px;height:34px;border-radius:10px;border:1.5px solid var(--navy);background:#fff;font:900 16px var(--display);color:var(--navy);cursor:pointer}
.step button:disabled{opacity:.35;cursor:default}
.step b{font:900 18px var(--display);min-width:26px;text-align:center}
.intg{display:flex;gap:8px;flex-wrap:wrap;margin:8px 0}
.intg button{font:700 13px var(--body);border-radius:999px;padding:7px 15px;border:1.5px solid var(--rule);background:#fff;color:var(--slate);cursor:pointer}
.intg button.on{background:var(--red);border-color:var(--red);color:#fff}
.mx{width:100%;border-collapse:separate;border-spacing:4px}
.mx td,.mx th{padding:0;border:0;background:none}
.mx .cell{width:100%;text-align:right;border:1.5px solid var(--rule);border-radius:12px;background:#fff;padding:10px 14px;cursor:pointer;font-family:var(--body)}
.mx .cell[disabled]{cursor:default;opacity:.55}
.mx .cell.on{border:2.5px solid var(--red);background:#fff6f7;opacity:1}
.mx .big{font:900 17px var(--display);color:var(--navy)}
.mx .big small{font:700 11px var(--body)}
.mx .sub{display:block;font:600 11px var(--body);color:var(--muted)}
.mx .sav{display:block;font:800 11px var(--display);color:var(--teal-ink)}
.totalrow{display:flex;align-items:flex-end;justify-content:space-between;gap:14px;flex-wrap:wrap;margin-top:6px}
.bigtotal{font:900 30px/1 var(--display);color:var(--red-ink);white-space:nowrap}
.bigtotal small{font:800 14px var(--display)}
.bigtotal .eq{display:block;font:700 13px var(--body);color:var(--muted);margin-top:3px}
.savebanner{margin-top:14px;background:#e9f7f1;border:1.5px solid #bfe7d4;border-radius:12px;padding:14px 18px;display:flex;align-items:center;gap:14px;flex-wrap:wrap}
.savebanner .amt{font:900 20px var(--display);color:var(--teal-ink)}
.savebanner .d{font:600 12.5px var(--body);color:var(--slate)}
.sigpad{width:100%;height:110px;border:1.5px dashed var(--rule);border-radius:10px;background:#fbfcfe;touch-action:none}
.signbtn{border:0;border-radius:12px;background:var(--red);color:#fff;font:800 15px var(--display);padding:13px 26px;cursor:pointer}
.signbtn:disabled{opacity:.5;cursor:default}
.ghostbtn{border:1.5px solid var(--rule);border-radius:10px;background:#fff;color:var(--slate);font:700 12.5px var(--body);padding:8px 14px;cursor:pointer}
.siginput{border:1.5px solid var(--rule);border-radius:10px;padding:10px 12px;font:600 14px var(--body);color:var(--navy);flex:1 1 200px}
details.tos{background:#fff;border:1.5px solid var(--rule);border-radius:18px;overflow:hidden}
details.tos > summary{cursor:pointer;list-style:none;padding:26px 32px;background:var(--band);display:flex;gap:18px;align-items:center}
details.tos > summary::-webkit-details-marker{display:none}
details.tos > summary .badge{flex:none;width:48px;height:48px;border-radius:50%;border:5px solid var(--navy);display:grid;place-items:center;font:900 18px var(--display);background:#fff;color:var(--navy)}
details.tos > summary h2{font:900 26px/1.1 var(--display);margin:0;color:var(--navy)}
details.tos > summary .hint{margin-left:auto;font:700 12px var(--body);color:var(--muted);white-space:nowrap}
details.tos[open] > summary .hint .cl{display:inline}
details.tos .part{border:0;border-radius:0}
.signedband{background:#e9f7f1;border:1.5px solid #bfe7d4;border-radius:12px;padding:12px 16px;font:700 13.5px var(--body);color:var(--teal-ink)}
.toastbar{position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:var(--navy);color:#fff;border-radius:10px;padding:10px 18px;font:600 13px var(--body);z-index:60}
/* Sticky pill nav (Lee, 6 Oct 2026): pin hard to the top with a solid wash
   so it rides above the boards exactly like the source doc. */
nav.parts{top:0;z-index:20;background:linear-gradient(180deg,#f2f5f9 85%,rgba(242,245,249,0))}
/* Inline fold for Part-A text sections (key commercial terms). */
details.fold{margin-top:30px;border-top:1px dashed #dfe6ef;padding-top:24px}
details.fold > summary{display:flex;align-items:baseline;gap:12px;cursor:pointer;list-style:none}
details.fold > summary::-webkit-details-marker{display:none}
details.fold > summary h3{margin:0;border:0;padding:0}
details.fold > summary .hint{font:700 12px var(--body);color:var(--muted);white-space:nowrap}
details.fold[open] > summary .hint{display:none}
`

interface CtData {
  companyName: string; customer: any; status: string; locked: boolean
  pricing: CtPriceBook; selections: CtSelections; options: any; proserv: any; computed: any
  signed: { name: string; title: string; at: string } | null
  countersigned: { name: string; at: string } | null
}

export const ContractPublicPage: React.FC = () => {
  const { slug = '' } = useParams()
  const token = new URLSearchParams(window.location.search).get('token') || ''
  const [data, setData] = useState<CtData | null>(null)
  const [err, setErr] = useState(false)
  const [sel, setSel] = useState<CtSelections | null>(null)
  const [toast, setToastRaw] = useState<string | null>(null)
  const setToast = (m: string) => { setToastRaw(m); setTimeout(() => setToastRaw(null), 3500) }
  const [signName, setSignName] = useState('')
  const [signTitle, setSignTitle] = useState('')
  const [drawn, setDrawn] = useState(false)
  const [signing, setSigning] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)

  const load = () => fetch(api(slug, token, 'data'))
    .then((r) => { if (!r.ok) throw new Error(); return r.json() })
    .then((d) => { setData(d.contract); setSel(d.contract.selections) })
    .catch(() => setErr(true))
  useEffect(() => { if (slug && token) load(); else setErr(true) /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug, token])

  const computed = useMemo(() => (data && sel ? ctCompute(data.pricing, sel) : null), [data, sel])

  if (err) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', background: '#eef2f7' }}>
      <div style={{ background: '#fff', border: '1.5px solid #d7e1ee', borderRadius: 14, padding: 28, textAlign: 'center', maxWidth: 380 }}>
        <p style={{ fontWeight: 800, color: '#1f2f3e', margin: 0 }}>Page not found</p>
        <p style={{ fontSize: 13, color: '#6f7a88' }}>This agreement link is not valid. Please contact WeQuote.</p>
      </div>
    </div>
  )
  if (!data || !sel || !computed) return <div style={{ minHeight: '100vh', background: '#eef2f7' }} />

  const book = data.pricing
  const money = moneyIn(book.currency)
  const locked = data.locked
  const opts = data.options || {}
  const canTerm = !!opts.chooseTerm && !locked
  const canFreq = !!opts.chooseFrequency && !locked
  // SOW switched off (Lee, 13 Oct 2026): Part B disappears, Terms become Part B.
  const hideSow = !!opts.hideSow
  // Frequency locked: the discount table shows ONLY the locked column.
  const freqs = (canFreq ? ['monthly', 'annual'] : [sel.frequency]) as Array<'monthly' | 'annual'>
  const navHtml = hideSow ? NAV_HTML.replace(/<a href="#sow">[\s\S]*?<\/a>/, '') : NAV_HTML
  // "Included in the price" lines show no money anywhere (Lee, 13 Oct 2026).
  const isInc = (k: string) => ((sel as any).includedKeys || []).includes(k)
  const save = (p: Partial<CtSelections>) => {
    setSel({ ...sel, ...p } as CtSelections)
    if (locked) return
    fetch(api(slug, token, 'select'), { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p) })
      .catch(() => setToast('Could not save your choice — check your connection.'))
  }

  /* Parties table: inject the real details into the doc's fill slots. */
  const c = data.customer || {}
  const partiesHtml = PARTIES_HTML
    .replace('<span class="fill">Customer legal name</span>', c.companyName || '<span class="fill">To be confirmed</span>')
    .replace('<span class="fill">Number</span>', c.companyNumber || '<span class="fill">To be confirmed</span>')
    .replace('<span class="fill">Address</span>', c.registeredOffice || '<span class="fill">To be confirmed</span>')
    .replace('<span class="fill">VAT number</span>', c.vatNumber || '<span class="fill">To be confirmed</span>')
    .replace('<span class="fill">Name, email</span>', c.noticesEmail || '<span class="fill">To be confirmed</span>')
    .replace('<span class="fill">WeQuote signatory</span>', 'Andrew Musgrave, am@wetech.cloud')
    .replace('<span class="fill">Name, role, email, mobile</span>', [c.repName, c.repTitle].filter(Boolean).join(', ') || '<span class="fill">To be confirmed</span>')

  /* signature pad */
  const padPos = (e: React.PointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect()
    return { x: (e.clientX - r.left) * (canvasRef.current!.width / r.width), y: (e.clientY - r.top) * (canvasRef.current!.height / r.height) }
  }
  const padDown = (e: React.PointerEvent) => {
    if (locked) return
    drawing.current = true
    const ctx = canvasRef.current!.getContext('2d')!
    const p = padPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y)
    ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1f2f3e'
  }
  const padMove = (e: React.PointerEvent) => {
    if (!drawing.current) return
    const ctx = canvasRef.current!.getContext('2d')!
    const p = padPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setDrawn(true)
  }
  const padClear = () => { const cv = canvasRef.current!; cv.getContext('2d')!.clearRect(0, 0, cv.width, cv.height); setDrawn(false) }
  const doSign = () => {
    if (!signName.trim()) { setToast('Enter your full name to sign.'); return }
    const cv = canvasRef.current!
    if (!drawn) {
      const ctx = cv.getContext('2d')!
      ctx.clearRect(0, 0, cv.width, cv.height)
      ctx.font = 'italic 42px "Segoe Script", "Brush Script MT", cursive'
      ctx.fillStyle = '#1f2f3e'
      ctx.fillText(signName.trim(), 20, 75)
    }
    setSigning(true)
    fetch(api(slug, token, 'sign'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: signName.trim(), title: signTitle.trim(), sigPng: cv.toDataURL('image/png') }) })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d?.error || 'HTTP ' + r.status); setToast('Signed — thank you!'); load() })
      .catch((e: Error) => setToast(e.message || 'Could not sign — try again.'))
      .finally(() => setSigning(false))
  }

  const shownPkgs = Object.entries(book.packages).filter(([k]) => !opts.shownPackages?.length || opts.shownPackages.includes(k) || sel.package === k)
  const pkg = book.packages[sel.package]
  const trainingDef = book.proserv.training[sel.training] || { label: '—', fee: 0, details: [] as string[] }

  /* The doc's amber fill-slots populated from the contract (Lee, 6 Oct 2026):
     term in the journey, training tier in the SOW. Discovery-only slots
     (legacy system name, import counts…) stay amber until known. */
  const journeyHtml = JOURNEY_HTML
    .replace('<span class="fill">12 / 24 / 36</span>', `<strong>${sel.termMonths}</strong>`)
    .replace('<span class="fill">30</span>', '30')
  const sowInner = (() => {
    // Fill slots in document order (Lee, 7 Oct 2026): staff answers from the
    // Studio replace each amber slot; the training slot fills itself from the
    // selection; anything unanswered stays amber.
    const fills: Record<string, string> = (data as any).sowFills || {}
    let n = -1
    let h = SOW_HTML.replace(/<span class="fill[^"]*">[^<]*<\/span>/g, (m) => {
      n += 1
      if (n === 17) return `<strong>${trainingDef.label}</strong>`
      let v = String(fills[String(n)] ?? '').trim()
      // Date-picker values arrive as yyyy-mm-dd — show them UK-style.
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) v = new Date(v + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      return v ? `<strong>${v.replace(/</g, '&lt;')}</strong>` : m
    })
    h = h.replace(/^[\s\S]*?<\/header>/, '')
    // ONLY the selected training package's row (Lee, 27 Oct 2026): the §2
    // table listed Basic/Bronze/Silver/Gold — non-selected tiers are dropped.
    for (const tier of ['Basic', 'Bronze', 'Silver', 'Gold']) {
      if (tier === trainingDef.label) continue
      h = h.replace(new RegExp(`<tr>\\s*<td>${tier}</td>[\\s\\S]*?</tr>`), '')
    }
    return h.replace(/^\s*<section[^>]*>/, '').replace(/<\/section>\s*$/, '')
  })()
  const termsInner = TERMS_HTML.replace(/^[\s\S]*?<\/header>/, '').replace(/^\s*<section[^>]*>/, '').replace(/<\/section>\s*$/, '')

  return (
    <>
      <style>{AGREEMENT_CSS + EXTRA_CSS}</style>
      <div className="wrap">
        {/* ── Header — matches the doc ── */}
        <header>
          <span className="eyebrow">WQ · Customer Agreement</span>
          <h1>Your WeQuote agreement</h1>
          <p className="lede">Prepared for <b>{c.companyName || 'you'}</b>. Your term starts the day you sign. We move your data off your old system, train your team, and you go live. Everything you're signing up to is set out below in {hideSow ? 'two parts' : 'three parts'}.</p>
        </header>
        <div dangerouslySetInnerHTML={{ __html: journeyHtml }} />
        <div dangerouslySetInnerHTML={{ __html: FACTS_HTML }} />
        <div dangerouslySetInnerHTML={{ __html: navHtml }} />

        {locked && (
          <div className="signedband" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 240 }}>
              ✓ Signed by {data.signed?.name}{data.signed?.title ? `, ${data.signed.title}` : ''} on {data.signed ? new Date(data.signed.at).toLocaleString('en-GB') : ''}
              {data.countersigned ? ` · countersigned by ${data.countersigned.name} — this agreement is complete.` : ' — awaiting WeQuote countersignature.'}
            </span>
            <a href={api(slug, token, 'pdf')} target="_blank" rel="noreferrer" style={{ font: '800 12px var(--display)', color: '#fff', background: 'var(--navy)', borderRadius: 10, padding: '8px 16px', textDecoration: 'none', whiteSpace: 'nowrap' }}>Download PDF ↓</a>
          </div>
        )}

        {/* ── PART A — Order Form (the live part) ── */}
        <section className="part" id="order">
          <div className="part-head"><span className="badge">A</span><div><span className="eyebrow">Part A</span><h2>Order Form</h2></div></div>
          <div className="part-body">
            <div dangerouslySetInnerHTML={{ __html: partiesHtml }} />

            <h3>2. Contract term and billing</h3>
            <div className="tw"><table><tbody>
              <tr><td>Contract term</td><td><strong>{sel.termMonths} months</strong>{canTerm ? ' — choose below' : ''}</td></tr>
              <tr><td>Commencement Date</td><td>The date WeQuote countersigns this Order Form</td></tr>
              <tr><td>Billing Start Date</td><td>The earlier of go-live or 30 days after the Commencement Date</td></tr>
              <tr><td>Payment frequency</td><td><strong>{sel.frequency === 'annual' ? 'Annually in advance' : 'Monthly in advance'}</strong>{canFreq ? ' — choose below' : ''}</td></tr>
              <tr><td>Payment method</td><td>Direct Debit / saved card for monthly. Invoice payable within 14 days for annual.</td></tr>
              <tr><td>Renewal</td><td>Renews for 12 months at the then-current 1-year price unless either party gives 90 days' written notice before the Expiry Date</td></tr>
              <tr><td>Price lock</td><td>Subscription Fees are fixed for the contract term</td></tr>
            </tbody></table></div>

            <h3>3. Committed subscription</h3>
            <p>These are the committed users, modules and package for the term. They can be increased at any time but not reduced until renewal.</p>

            {/* Plan — fixed (default) or the customer picks from the plans we show. */}
            {opts.choosePackage && !locked ? (
              <div className="pickgrid">
                {shownPkgs.map(([k, p]) => (
                  <button key={k} className={`pick ${sel.package === k ? 'on' : ''}`} onClick={() => save({ package: k })}>
                    <span className="nm">{p.label}</span>
                    <span className="pr">{money(p.monthly)} <small>/month</small></span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="fixedrow"><span className="nm">{pkg?.label || '—'}</span><span className="tagfix">Selected for you</span><span className="pr">{money(pkg?.monthly || 0)} /mo</span></div>
            )}

            {/* Snagg users */}
            {opts.chooseSnagg && !locked ? (
              <div className="fixedrow">
                <span className="nm">Snagg Technicians <small style={{ font: '600 12px var(--body)', color: 'var(--muted)' }}>{money(book.snagg.perUserMonthly)} per user / month</small></span>
                <span className="step">
                  <button disabled={sel.snaggUsers <= 0} onClick={() => save({ snaggUsers: Math.max(0, sel.snaggUsers - 1) })}>−</button>
                  <b>{sel.snaggUsers}</b>
                  <button onClick={() => save({ snaggUsers: Math.min(book.snagg.max, sel.snaggUsers + 1) })}>+</button>
                </span>
              </div>
            ) : (
              <div className="fixedrow"><span className="nm">Snagg Technicians × {sel.snaggUsers}</span><span className="tagfix">{isInc('snagg') ? 'Included in your price' : 'Set for you'}</span><span className="pr">{isInc('snagg') ? 'Included' : `${money(book.snagg.perUserMonthly * sel.snaggUsers)} /mo`}</span></div>
            )}

            {/* Modules */}
            {opts.chooseModules && !locked ? (
              Object.entries(book.modules).map(([k, m]) => (
                <div key={k} className="fixedrow">
                  <span className="nm">{m.label}</span>
                  <span className="intg" style={{ margin: 0 }}>
                    <button className={sel.modules.includes(k) ? 'on' : ''} onClick={() => save({ modules: sel.modules.includes(k) ? sel.modules.filter((x) => x !== k) : [...sel.modules, k] })}>
                      {sel.modules.includes(k) ? '✓ Included' : 'Add'} · {money(m.monthly)}/mo
                    </button>
                  </span>
                </div>
              ))
            ) : (
              sel.modules.length > 0
                ? sel.modules.map((k) => book.modules[k] && <div key={k} className="fixedrow"><span className="nm">{book.modules[k].label}</span><span className="tagfix">{isInc(`module:${k}`) ? 'Included in your price' : 'Included for you'}</span><span className="pr">{isInc(`module:${k}`) ? 'Included' : `${money(book.modules[k].monthly)} /mo`}</span></div>)
                : null
            )}

            {/* Integrations */}
            {opts.chooseIntegrations && !locked ? (
              <>
                <p style={{ margin: '12px 0 0' }}><strong>Integrations</strong> — {money(10)} each / month</p>
                <div className="intg">
                  {Object.entries(book.integrations).map(([k, m]) => {
                    const on = sel.integrations.includes(k)
                    return <button key={k} className={on ? 'on' : ''} onClick={() => save({ integrations: on ? sel.integrations.filter((x) => x !== k) : [...sel.integrations, k] })}>{on ? '✓ ' : ''}{m.label} · {money(m.monthly)}</button>
                  })}
                </div>
              </>
            ) : (
              sel.integrations.length > 0 && (
                <div className="fixedrow">
                  <span className="nm">Integrations: {sel.integrations.map((k) => book.integrations[k]?.label).filter(Boolean).join(', ')}</span>
                  <span className="tagfix">{sel.integrations.every((k) => isInc(`integration:${k}`)) ? 'Included in your price' : 'Included for you'}</span>
                  <span className="pr">{(() => { const due = sel.integrations.reduce((a, k) => a + (isInc(`integration:${k}`) ? 0 : (book.integrations[k]?.monthly || 0)), 0); return due > 0 ? `${money(due)} /mo` : 'Included' })()}</span>
                </div>
              )
            )}

            <h3>4. Term discounts</h3>
            <p>{canTerm || canFreq ? 'Click an option — every figure below is your exact basket at that term.' : 'Your term and payment frequency have been set for you; the table shows how your price compares.'} Discounts are measured against the standard price (12 months, paid monthly) and are earned by completing the full term.</p>
            <table className="mx">
              <thead><tr><th style={{ padding: '6px 10px' }}>Contract term</th>{freqs.includes('monthly') && <th style={{ padding: '6px 10px', textAlign: 'right' }}>Paid monthly</th>}{freqs.includes('annual') && <th style={{ padding: '6px 10px', textAlign: 'right' }}>Paid annually in advance</th>}</tr></thead>
              <tbody>
                {computed.matrix.map((row: any) => (
                  <tr key={row.termMonths}>
                    <td style={{ font: '800 15px var(--display)', color: 'var(--navy)', padding: '10px' }}>{row.termMonths} months</td>
                    {freqs.map((f) => {
                      const cell = f === 'monthly' ? row.monthly : row.annual
                      const on = sel.termMonths === row.termMonths && sel.frequency === f
                      const allowed = (canTerm || row.termMonths === sel.termMonths) && (canFreq || f === sel.frequency)
                      const saving = Math.round((computed.standardMonthly - cell.total) * row.termMonths * 100) / 100
                      return (
                        <td key={f}>
                          <button
                            className={`cell ${on ? 'on' : ''}`}
                            disabled={locked || !allowed}
                            onClick={() => save({ ...(canTerm ? { termMonths: row.termMonths as 12 | 24 | 36 } : {}), ...(canFreq ? { frequency: f } : {}) })}
                          >
                            {f === 'monthly'
                              ? <span className="big">{money(cell.total)}<small>/mo</small></span>
                              : <><span className="big">{money(cell.perYear)}<small>/yr</small></span><span className="sub">= {money(cell.total)}/mo</span></>}
                            <span className="sav">{saving > 0 ? `save ${money(saving)} over ${row.termMonths} months (−${cell.pct}%)` : 'standard price'}</span>
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <h3>5. Professional Services (one-off fees)</h3>
            <div className="fixedrow">
              <span className="nm">Implementation & Training — {trainingDef.label}<span className="tagfix" style={{ marginLeft: 8 }}>Selected for you</span>
                {!!trainingDef.details?.length && (
                  <div style={{ margin: '10px 0 2px' }}>
                    <p style={{ margin: '0 0 7px', font: '800 10px var(--display)', letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted)' }}>What's included</p>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '6px 22px' }}>
                      {trainingDef.details.map((d: string) => (
                        <li key={d} style={{ font: '500 12.5px/1.45 var(--body)', color: 'var(--navy)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <span aria-hidden style={{ color: 'var(--teal-ink)', fontWeight: 800, lineHeight: '1.4' }}>✓</span>
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </span>
              <span className="pr">{trainingDef.fee ? money(trainingDef.fee) : 'Included'}</span>
            </div>
            <div className="tw"><table>
              <thead><tr><th>Service</th><th>Fee</th><th>When payable</th></tr></thead>
              <tbody>
                <tr><td>Data Migration & System Close-Down</td><td><strong>{money(Number(data.proserv?.migrationFee || 0))}</strong> fixed</td><td>100% on signing</td></tr>
                {Number(data.proserv?.scannerCount || 0) > 0 && (
                  <tr><td>Barcode scanners × {Number(data.proserv?.scannerCount)}</td><td><strong>{money(Number(data.proserv?.scannerCount) * Number(data.proserv?.scannerEach ?? 329))}</strong> ({money(Number(data.proserv?.scannerEach ?? 329))} each)</td><td>In full on signing</td></tr>
                )}
                <tr><td>Additional hours — Implementation & Training{hideSow ? '' : ' (outside the Statement of Works)'}</td><td>{Number(data.proserv?.extraDay || 0) > 0 ? <>{money(Number(data.proserv?.extraDay))} per day · </> : null}{money(Number(data.proserv?.extraHour || 0))} per hour</td><td>Paid in advance for a set number of hours</td></tr>
              </tbody>
            </table></div>
            <p>Professional Services fees are non-refundable once WeQuote starts the work, including the discovery call. They are separate from the Subscription Fees, which remain payable for the full term.</p>

            {/* 6 · Key commercial terms — collapsible like the other text
                sections (Lee, 6 Oct 2026); the numbered heading stays visible
                as the fold's summary so the 1–7 numbering never breaks. */}
            <details className="fold">
              <summary><h3>6. Key commercial terms</h3><span className="hint">Click to read ▾</span></summary>
              <div dangerouslySetInnerHTML={{ __html: KEY_TERMS_HTML.replace('<h3>6. Key commercial terms</h3>', '') }} />
            </details>

            {/* The committed summary sits RIGHT BEFORE Acceptance (Lee, 6 Oct
                2026) — the last thing they read is exactly what they sign. */}
            <h3>Your committed subscription — what you're signing</h3>
            <div className="tw"><table>
              <thead><tr><th>Item</th><th>Qty</th><th>Standard price / month</th><th>Term discount</th><th>Your price / month</th></tr></thead>
              <tbody>
                {computed.lines.map((l: any) => (
                  <tr key={l.key}><td>{l.label}</td><td>{l.qty}</td><td>{l.included ? '—' : money(l.standardMonthly)}</td><td>{l.included ? '—' : l.discountPct ? `−${l.discountPct}%` : '—'}</td><td><strong>{l.included ? 'Included' : money(l.yourMonthly)}</strong></td></tr>
                ))}
                <tr><td colSpan={4}><strong>Total Subscription Fees</strong></td><td><strong>{money(computed.monthlyTotal)} /mo</strong></td></tr>
              </tbody>
            </table></div>
            <div className="totalrow">
              <p style={{ margin: 0, maxWidth: '46ch' }}>{sel.frequency === 'annual' ? 'Annual payers are invoiced 12 × the monthly price at the start of each Contract Year.' : 'Billed monthly in advance by Direct Debit or saved card.'} {book.vatNote}</p>
              <span className="bigtotal">
                {sel.frequency === 'annual'
                  ? <>{money(computed.billedAnnually)}<small> /yr</small><span className="eq">= {money(computed.monthlyTotal)} /mo</span></>
                  : <>{money(computed.monthlyTotal)}<small> /mo</small></>}
              </span>
            </div>
            {computed.savingPerMonth > 0 && (
              <div className="savebanner">
                <div>
                  <span className="amt">You're saving {money(computed.savingOverTerm)} over your {sel.termMonths}-month term</span>
                  <span className="d" style={{ display: 'block' }}>{money(computed.savingPerMonth)} off every month (−{computed.discountPct}%) against the standard price — locked in for the whole term.</span>
                </div>
              </div>
            )}

            <h3>7. Acceptance</h3>
            <p>By signing, you confirm you have read and agree to this Order Form{hideSow ? ' and the WeQuote Terms of Service (Part B)' : ', the Statement of Works (Part B) and the WeQuote Terms of Service (Part C)'}. Your signature, the date/time and your IP address are recorded.</p>
            <div className="sig">
              <div className="sigcard">
                <span className="eyebrow">Customer</span>
                {locked ? (
                  <>
                    <div className="sigline"><small>Signed by</small><b style={{ font: '800 16px var(--display)', color: 'var(--navy)' }}>{data.signed?.name}{data.signed?.title ? `, ${data.signed.title}` : ''}</b></div>
                    <div className="sigline"><small>Date</small>{data.signed ? new Date(data.signed.at).toLocaleString('en-GB') : ''}</div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' }}>
                      <input className="siginput" value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Full name *" />
                      <input className="siginput" value={signTitle} onChange={(e) => setSignTitle(e.target.value)} placeholder="Job title (e.g. Director)" />
                    </div>
                    <canvas ref={canvasRef} width={700} height={110} className="sigpad"
                      onPointerDown={padDown} onPointerMove={padMove} onPointerUp={() => { drawing.current = false }} onPointerLeave={() => { drawing.current = false }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0 14px', flexWrap: 'wrap' }}>
                      <button className="ghostbtn" onClick={padClear}>Clear</button>
                      <span style={{ font: '600 11.5px var(--body)', color: 'var(--muted)' }}>Draw your signature — or leave blank to sign with your typed name.</span>
                      <span style={{ flex: 1 }} />
                      <button className="signbtn" disabled={signing || !signName.trim()} onClick={doSign}>
                        {signing ? 'Signing…' : `Sign — ${sel.frequency === 'annual' ? `${money(computed.billedAnnually)}/yr` : `${money(computed.monthlyTotal)}/mo`} for ${sel.termMonths} months`}
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="sigcard">
                <span className="eyebrow">WeQuote</span>
                {data.countersigned ? (
                  <>
                    <div className="sigline"><small>Countersigned by</small><b style={{ font: '800 16px var(--display)', color: 'var(--navy)' }}>{data.countersigned.name}</b></div>
                    <div className="sigline"><small>Date</small>{new Date(data.countersigned.at).toLocaleString('en-GB')}</div>
                  </>
                ) : (
                  <div className="sigline big"><small>Countersignature</small><span style={{ color: 'var(--muted)', fontSize: 13 }}>WeQuote countersigns after you — the Commencement Date is the countersign date.</span></div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── PART B — Statement of Works, collapsible — hidden entirely when
            the SOW is switched off for this contract (Lee, 13 Oct 2026) ── */}
        {!hideSow && (
          <details className="tos" id="sow">
            <summary>
              <span className="badge">B</span>
              <h2>Statement of Works</h2>
              <span className="hint">Click to read ▾</span>
            </summary>
            <section className="part" style={{ border: 0, borderRadius: 0 }}>
              <div dangerouslySetInnerHTML={{ __html: sowInner }} />
            </section>
          </details>
        )}

        {/* ── Terms of Service — Part C, or Part B when the SOW is off ── */}
        <details className="tos terms" id="terms">
          <summary>
            <span className="badge">{hideSow ? 'B' : 'C'}</span>
            <h2>WeQuote Terms of Service</h2>
            <span className="hint">Click to read the full terms ▾</span>
          </summary>
          <section className="part terms" style={{ border: 0, borderRadius: 0 }}>
            <div dangerouslySetInnerHTML={{ __html: termsInner }} />
          </section>
        </details>

        <div dangerouslySetInnerHTML={{ __html: CONTACT_HTML }} />
      </div>
      <div dangerouslySetInnerHTML={{ __html: FOOTER_HTML }} />
      {toast && <div className="toastbar">{toast}</div>}
    </>
  )
}
